import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Send, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SenderValidationModal from '@/components/quick-send/SenderValidationModal';

interface ReviewStepProps {
  documentTitle: string;
  fieldsCount: number;
  recipientName: string;
  recipientEmail: string;
  recipientMobile: string;
  senderName: string;
  senderEmail: string;
  senderMobile: string;
  sending: boolean;
  senderVerified: boolean;
  onSenderVerified: () => void;
  onBack: () => void;
  onSend: () => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  documentTitle, fieldsCount,
  recipientName, recipientEmail, recipientMobile,
  senderName, senderEmail, senderMobile,
  sending, senderVerified, onSenderVerified, onBack, onSend,
}) => {
  const { t } = useTranslation('quicksend');
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="max-w-lg mx-auto">
      <h2 className="text-2xl font-bold text-navy text-center mb-2">{t('review_title')}</h2>
      <p className="text-sm text-gray-500 text-center mb-6">{t('review_subtitle', 'Review the details below before sending')}</p>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 mb-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">{t('review_document')}</span>
          <span className="text-sm font-medium text-gray-800 text-right">{documentTitle}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">{t('review_fields_count', { count: fieldsCount })}</span>
        </div>
        <hr />
        <div>
          <span className="text-sm text-gray-500">{t('review_recipient')}</span>
          <p className="text-sm font-medium text-gray-800">{recipientName}</p>
          <p className="text-xs text-gray-500">{recipientEmail}</p>
          {recipientMobile && <p className="text-xs text-gray-500">{recipientMobile}</p>}
        </div>
        <hr />
        <div className="flex items-start justify-between">
          <div>
            <span className="text-sm text-gray-500">{t('review_sender')}</span>
            <p className="text-sm font-medium text-gray-800">{senderName}</p>
            <p className="text-xs text-gray-500">{senderEmail}</p>
            {senderMobile && <p className="text-xs text-gray-500">{senderMobile}</p>}
          </div>
          <div className="ml-4 flex-shrink-0 pt-1">
            {senderVerified ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                <ShieldCheck className="h-3.5 w-3.5" />
                {t('validate_validated')}
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setModalOpen(true)}
                className="text-xs"
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                {t('validate_button')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {!senderVerified && (
        <p className="text-xs text-amber-600 text-center mb-3">{t('validate_required')}</p>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('step_details')}
        </Button>
        <Button onClick={onSend} disabled={sending || !senderVerified} className="gap-2 bg-green hover:bg-green-dark text-white">
          {sending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('review_sending')}
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              {t('review_send_button')}
            </>
          )}
        </Button>
      </div>

      <SenderValidationModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        senderEmail={senderEmail}
        senderMobile={senderMobile}
        onVerified={onSenderVerified}
      />
    </div>
  );
};

export default ReviewStep;
